class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.memberDiscountHandoff();
  }

  // Member discount handoff (see the rendered signals in cart-drawer.liquid).
  // Shopify does NOT recalculate a cart's automatic member discount when a guest
  // logs in — the cart keeps its pre-login totals until it is next written to. So:
  //   • While a NON-member has eligible savings in the cart, we keep a localStorage
  //     marker set ("member savings pending"), refreshed as the cart changes.
  //   • When they return as a MEMBER, that marker means the cart was built without
  //     the discount: we re-send the current line quantities (a real cart write,
  //     no visible change) to force the recalculation, patch the sections, then
  //     clear the marker on success. Members only ever consume/clear it.
  // localStorage (not sessionStorage) so the marker survives the login redirect,
  // other tabs, and a delayed login while the cart is still alive server-side.
  memberDiscountHandoff() {
    const KEY = 'rj_pending_member_savings';
    const setMarker = (on) => {
      try {
        if (on) localStorage.setItem(KEY, '1');
        else localStorage.removeItem(KEY);
      } catch (e) {
        /* storage unavailable — nothing we can do */
      }
    };

    if (!this.hasAttribute('data-member')) {
      // NON-member: keep the pending marker in sync with the cart.
      setMarker(this.hasAttribute('data-pending-eligible'));

      // Cart changes (add / qty / remove / cross-sell / quick-add) all publish
      // cartUpdate — re-derive the marker from the freshly rendered drawer section
      // when the payload carries it, otherwise fall back to cart emptiness.
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          const cartData = event && event.cartData;
          const sectionHtml = cartData && cartData.sections && cartData.sections['cart-drawer'];
          if (sectionHtml) {
            const fresh = new DOMParser().parseFromString(sectionHtml, 'text/html').querySelector('cart-drawer');
            if (fresh) {
              setMarker(fresh.hasAttribute('data-pending-eligible'));
              return;
            }
          }
          // No fresh drawer section in this payload — clear only when the cart is
          // definitively empty, otherwise keep the marker set (err toward syncing).
          setMarker(!(cartData && cartData.item_count === 0));
        });
      }
      return;
    }

    // MEMBER: only act on a marker left behind by the non-member phase.
    let pending = false;
    try {
      pending = !!localStorage.getItem(KEY);
    } catch (e) {
      pending = false;
    }
    if (!pending) return;

    if (!this.hasAttribute('data-member-sync')) {
      setMarker(false); // empty cart — nothing to sync, clear the stale marker
      return;
    }
    if (!window.routes || !window.routes.cart_url) return;

    const quantities = this.dataset.memberSync
      .split(',')
      .map((q) => parseInt(q, 10))
      .filter((q) => !isNaN(q));
    if (!quantities.length) {
      setMarker(false);
      return;
    }

    fetch(`${window.routes.cart_url}/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates: quantities, sections: 'cart-drawer,main-cart-items' }),
    })
      .then((response) => response.json())
      .then((data) => {
        const sections = (data && data.sections) || {};

        if (sections['cart-drawer']) {
          const drawerDoc = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
          ['cart-drawer-items', '.cart-drawer__footer', '.drawer__heading'].forEach((selector) => {
            const target = document.querySelector(selector);
            const source = drawerDoc.querySelector(selector);
            if (target && source) target.replaceWith(source);
          });
        }

        if (sections['main-cart-items']) {
          const pageDoc = new DOMParser().parseFromString(sections['main-cart-items'], 'text/html');
          const source = pageDoc.querySelector('#main-cart-items');
          const target = document.getElementById('main-cart-items');
          if (source && target) target.innerHTML = source.innerHTML;
        }

        setMarker(false); // clear only after a successful sync
      })
      .catch(() => {
        /* leave the marker set so it retries on the next member page load */
      });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    document.dispatchEvent(new CustomEvent('cart-drawer:open'));

    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');

    // Load cross-sell whenever the placeholder is empty and the cart has items.
    // Covers: page refresh → cart icon click, post-ATC (renderContents resets the div to empty).
    const crossSell = document.querySelector('#CartDrawer-CrossSell');
    if (
      crossSell &&
      crossSell.children.length === 0 &&
      !crossSell.hasAttribute('data-loaded') &&
      !this.classList.contains('is-empty')
    ) {
      CartDrawer.loadCrossSell();
    }
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    // `is-empty` lives on the <cart-drawer> custom element itself in this theme
    // (snippets/cart-drawer.liquid sets `class="drawer{% if cart == empty %} is-empty{% endif %}"`),
    // not on `.drawer__inner` like stock Dawn. All empty-state CSS targets `cart-drawer.is-empty .*`
    // (header, mini-cart-table, footer, cross-sell all set to display:none). renderContents() is only
    // called after a successful /cart/add.js response — so the cart cannot be empty here — and the new
    // innerHTML we just swapped in has no `.drawer__inner-empty` sibling. Result without this line:
    // PDP cross-sell ATC from an empty cart renders a blank drawer (everything hidden, nothing to show).
    this.classList.remove('is-empty');
    this.productId = parsedState.id;
    // Shopify returns a section as null when it fails to render (the add itself
    // still succeeded): render what came back, then refresh once.
    const missing = this.applySections(parsedState.sections || {});
    if (missing.length) this.refreshSections();

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  // Section ids to request with /cart/add (bundled section rendering, max 5):
  // the drawer, the cart icon and, when the drawer has a cross-sell slot, the
  // cross-sell — so opening the drawer after an add fetches nothing.
  getSectionIdsForAdd() {
    const ids = this.getSectionsToRender().map((section) => section.id);
    if (document.getElementById('CartDrawer-CrossSell')) ids.push(CartDrawer.CROSS_SELL_SECTION);
    return ids;
  }

  // Renders sections from a Cart API / Section Rendering API payload. Returns
  // the ids that came back null (or missing) so the caller can refresh them.
  applySections(sections) {
    const missing = [];
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!sectionElement) return;
      if (!sections[section.id]) {
        missing.push(section.id);
        return;
      }
      sectionElement.innerHTML = this.getSectionInnerHTML(sections[section.id], section.selector);
    });
    if (sections[CartDrawer.CROSS_SELL_SECTION]) CartDrawer.fillCrossSell(sections[CartDrawer.CROSS_SELL_SECTION]);
    return missing;
  }

  // One Section Rendering API request for everything the drawer shows. Resolves
  // with the sections (null on failure). Also syncs the drawer's empty state,
  // which lives on <cart-drawer> itself, outside the replaced markup.
  refreshSections() {
    if (!window.routes || !window.routes.cart_url) return Promise.resolve(null);
    return fetch(`${window.routes.cart_url}?sections=${this.getSectionIdsForAdd().join(',')}`)
      .then((response) => response.json())
      .then((sections) => {
        this.applySections(sections);
        const fresh = sections['cart-drawer'] && this.getSectionDOM(sections['cart-drawer'], 'cart-drawer');
        if (fresh) this.classList.toggle('is-empty', fresh.classList.contains('is-empty'));
        return sections;
      })
      .catch((e) => {
        console.error(e);
        return null;
      });
  }

  static fillCrossSell(html) {
    const target = document.querySelector('#CartDrawer-CrossSell');
    if (!target) return;
    const source = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
    target.innerHTML = source ? source.innerHTML : '';
    // Rendered (possibly with no products): open() must not fetch it again.
    target.setAttribute('data-loaded', '');
  }

  static loadCrossSell() {
    if (!window.routes || !window.routes.cart_url) return;
    const target = document.querySelector('#CartDrawer-CrossSell');
    if (!target) return;
    fetch(`${window.routes.cart_url}?section_id=cart-cross-sell-drawer`)
      .then((r) => r.text())
      .then((html) => {
        const source = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
        if (source) target.innerHTML = source.innerHTML;
        else target.innerHTML = '';
      })
      .catch(() => {});
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

CartDrawer.CROSS_SELL_SECTION = 'cart-cross-sell-drawer';
customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: 'cart-drawer-items',
      },
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.cart-drawer__footer',
      },
      {
        // Drawer header text ("Your Cart : N Item(s)" or just "Your Cart" when empty)
        // lives outside <cart-drawer-items> and .cart-drawer__footer, so it would
        // otherwise stay stale on every per-section innerHTML replacement (qty change,
        // remove-non-last item, drawer cross-sell ATC, cross-instance pub/sub). Adding
        // it here lets the same loop in cart.js's updateQuantity refresh it.
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__heading',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
